import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import React, { useContext, useMemo } from 'react';
import { translate } from '../../../../../../types/translation/Translator';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { CustomFieldEntityType } from '../../../../../../types/custom_field/customField';
import { StockLocationRoleAssignmentContext } from '../../../../../../context/StockLocationRoleAssignmentContext';
import { StockLocationRole } from '../../../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import { TagContext } from '../../../../../../context/TagContext';
import { EntityType } from '../../../../../../types/comment';
import { StockLocation } from '../../../../../../types/stock_location/stockLocation';
import { toMap } from '../../../../../../util/map.util';
import { OrderInput, OrderUtil } from '../../../../../../types/order/';
import { ContactContext } from '../../../../../../context/ContactContext';
import DropdownSearchSelect from '../../../../../components/Dropdown/DropdownSearchSelect';
import { Icon, IconButton } from '../../../../../icons';
import { IconType } from '../../../../../icons/Icon';
import { AddressUtil } from '../../../../../../types/common/address.util';
import { NextButton } from '../../../../../components/Button';
import CustomFieldOrderInput from '../../../../Common/CustomFieldOrderInput';
import { FlowType, OrderFlow } from '../../../../../../types/order/orderFlow';
import { OrderFlowContext } from '../../../../../../context/OrderFlowContext';

interface CreateOrderInfoPaneProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  next: () => void;

  flowIds?: string[];
}

export default function CreateOrderInfoPane({ order, setOrder, next, flowIds = [] }: CreateOrderInfoPaneProps) {
  const { currentCompany, companies } = useContext(CompanyContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { customFields } = useContext(CustomFieldContext);
  const { tags } = useContext(TagContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { contacts } = useContext(ContactContext);
  const { orderFlows } = useContext(OrderFlowContext);

  const orderFeatureToggles = currentCompany.settings.featureToggles.orders;
  const showContact =
    (currentCompany.settings.featureToggles.orders.customer && order.type === FlowType.outbound) ||
    (currentCompany.settings.featureToggles.orders.supplier && order.type === FlowType.inbound);

  const disabled = () => {
    switch (order.type) {
      case FlowType.inbound:
      case FlowType.replenish:
        if (!order.toStockLocationId) return true;
        break;
      case FlowType.outbound:
      case FlowType.move:
      case FlowType.disposal:
        if (!order.fromStockLocationId) return true;
        break;
      case FlowType.move:
        if (!order.fromStockLocationId || !order.toStockLocationId) return true;
        break;
    }

    if (!orderFeatureToggles.productSelectionFirst) {
      if (
        !![...customFields.values()]
          .filter(
            cf =>
              !cf.hidden &&
              !cf.generated &&
              cf.mandatory &&
              cf.entityType === CustomFieldEntityType.order &&
              !cf.onCreate &&
              (!cf.entitySubtype || cf.entitySubtype.toString() === order.type.toString()),
          )
          .filter(cf => !order.customFields.has(cf.id) || !order.customFields.get(cf.id)?.value).length
      )
        return true;
    }

    return false;
  };

  const authorizedStockLocations = useMemo(() => {
    return [...stockLocations.values()].filter(
      s =>
        s.companyId === currentCompany.id &&
        hasStockLocationRole(s.companyId, s.id, StockLocationRole.STOCK_LOCATION_SUPERVISOR),
    );
  }, [stockLocations]);

  const destinationStockLocations = useMemo(() => {
    return order.type === FlowType.move ? stockLocations : toMap(authorizedStockLocations, 'id');
  }, [authorizedStockLocations, order]);

  const sourceStockLocations = useMemo(() => {
    const stockLocationTags = [...tags.values()]
      .filter(tag => tag.type === EntityType.stockLocation)
      .map(tag => new StockLocation({ companyId: tag.companyId, id: `tag::${tag.id}`, name: tag.name }));

    return toMap([...authorizedStockLocations, ...stockLocationTags], 'id');
  }, [tags, authorizedStockLocations, currentCompany]);

  const SourceLocationSelect = () => (
    <div className='shrink-0 grow-0 basis-1/2'>
      <DropdownSearchSelect
        mandatory
        label={translate({ key: TranslationKey.sourceStockLocation })}
        placeholder={translate({ key: TranslationKey.sourceStockLocation })}
        value={sourceStockLocations.get(order.fromStockLocationId || '')}
        items={Array.from(sourceStockLocations.values())}
        textBuilder={item => item.name}
        onSelect={item => setOrder(order.withFromStockLocationId(item?.id))}
        itemBuilder={item => {
          const leading = () => {
            if (item.id.includes('tag::')) {
              return <Icon size='size-5' type={IconType.tag1} />;
            } else {
              return <Icon size='size-5' type={IconType.building3} />;
            }
          };

          return (
            <div className='flex items-center gap-2 stroke-current'>
              {leading()}
              <p>{item.name}</p>
              {currentCompany.partner ? <p className='ml-1'>({companies.get(item.companyId)?.name})</p> : null}
            </div>
          );
        }}
      />
    </div>
  );

  const DestinationLocationSelect = () => (
    <div className='grow-0 basis-1/2'>
      <DropdownSearchSelect
        mandatory
        label={translate({ key: TranslationKey.destinationStockLocation })}
        placeholder={translate({ key: TranslationKey.destinationStockLocation })}
        value={destinationStockLocations.get(order.toStockLocationId || '')}
        items={Array.from(destinationStockLocations.values())}
        textBuilder={item =>
          currentCompany.partner ? `${item.name} (${companies.get(item.companyId)?.name})` : item.name
        }
        onSelect={item => setOrder(order.withToStockLocationId(item?.id))}
      />
    </div>
  );

  const StockLocationSwitcher = () => (
    <IconButton
      className='mt-6'
      type={IconType.switchHorizontal01}
      text={translate({ key: TranslationKey.swapStockLocations })}
      onClick={() => {
        const from = order.fromStockLocationId;
        order.fromStockLocationId = order.toStockLocationId;
        order.toStockLocationId = from;
        setOrder(new OrderInput(order));
      }}
    />
  );

  const validFlows = Array.from(orderFlows.values()).filter(flow => flowIds.includes(flow.id) || !flowIds.length);
  return (
    <div className='flex h-full flex-col justify-between px-4 py-4'>
      <div className='flex flex-col gap-2'>
        <div className='grid grid-cols-2'>
          <DropdownSearchSelect<OrderFlow>
            label={translate({ key: TranslationKey.orderType })}
            placeholder={translate({ key: TranslationKey.orderType })}
            value={orderFlows.get(order.flowId)}
            items={validFlows.sort((a, b) => a.name.localeCompare(b.name))}
            textBuilder={item => item.name}
            onSelect={v => {
              setOrder(order.withFlow(v));
            }}
          />
        </div>
        <div className='flex gap-2'>
          {OrderUtil.hasFromStockLocation(order) && <SourceLocationSelect />}
          {order.type === FlowType.move && <StockLocationSwitcher />}
          {OrderUtil.hasToStockLocation(order) && <DestinationLocationSelect />}
        </div>
        <div className='grid grid-cols-2 gap-2'>
          {showContact && (
            <>
              <div className='col-span-1'>
                <DropdownSearchSelect
                  value={contacts.get(order.contactId || '')}
                  onSelect={item => setOrder(order.withContactId(item?.id))}
                  label={translate({ key: TranslationKey.contact })}
                  placeholder={translate({ key: TranslationKey.contact })}
                  items={Array.from(contacts.values())}
                  textBuilder={item => item.name}
                />
              </div>
              {order.contactId && (
                <div className='col-span-1'>
                  <DropdownSearchSelect
                    value={order.address}
                    onSelect={item => setOrder(order.withAddress(item))}
                    label={translate({ key: TranslationKey.address })}
                    placeholder={translate({ key: TranslationKey.address })}
                    items={contacts.get(order.contactId || '')?.addresses || []}
                    textBuilder={item => AddressUtil.addressToSearchQuery(item)}
                    itemBuilder={item => {
                      return (
                        <div>
                          <p className='text-xs text-gray-400'>{`${item.type}`}</p>
                          {item.name && <p>{`${item.name}`}</p>}
                          {item.addressLine1 && <p>{`${item.addressLine1}`}</p>}
                          {item.addressLine2 && <p>{`${item.addressLine2}`}</p>}
                          {(item.postalCode || item.city) && <p>{`${item.postalCode} ${item.city}`.trim()}</p>}
                          {item.region && <p>{`${item.region}`}</p>}
                          {item.countryCode && <p>{`${item.countryCode}`}</p>}
                        </div>
                      );
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
        <CustomFieldOrderInput order={order} setOrder={setOrder} />
      </div>
      <div>
        <div className='flex justify-end'>
          <NextButton disabled={disabled()} onClick={next} />
        </div>
      </div>
    </div>
  );
}
